import { useMutation, useQuery } from "@tanstack/react-query";
import {
  AssetFrontendSupportVideoCallsApi,
  AssetFrontendSupportVideoCallsApi$RequestCallResultState,
  PublicAssetFrontendApi$AssetComponentDto,
} from "@/custolink-transpiled_by_server/bc.asset_interaction.api_methods";
import useUiContext from "@/custolink-hooks/useUiContext";
import { useEffect, useState } from "react";
import Loading from "@/components/shared/Loading";
import RdaLoading from "@/custolink-views/asset_frontend/redesigns/RdaLoading";
import { useTranslation } from "react-i18next";
import { PiVideoCamera } from "react-icons/pi";
import { AssetViewDetails } from "@/custolink-views/asset_frontend/asset_view/useAssetViewFacade";
import { VideoRoom, VideoRoomEndedInfo } from "@/custolink-views/shared/VideoRoomPage";
import { MobileTechnicianRoom } from "@/custolink-newdesign/pages/support/room/MobileTechnicianRoom";
import { useAssetFrontendDesign } from "@/custolink-views/asset_frontend/redesigns/assetFrontendDesign";
import {
  PrimaryButton,
  SecondaryButton,
} from "@/custolink-views/asset_frontend/redesigns/formKit";

const State = AssetFrontendSupportVideoCallsApi$RequestCallResultState;

/**
 * Waiting and Active both come back from request_call with a token: the customer sits in
 * their own room from the start, and the room's own "Connecting you to support" overlay is
 * what tells them nobody has arrived yet. There is no accept step to wait for any more, so
 * this hook only tracks whether the session is still joinable, whether the customer has
 * temporarily left the LiveKit room, and whether the session itself has ended.
 *
 * The session is NOT requested on mount - the customer must actively start the call. The
 * request runs exactly once, only after that, and never on window focus/reconnect, so
 * opening the component can never create an orphaned session.
 */
const videoPageHook = (assetView: AssetViewDetails) => {
  const uic = useUiContext();

  // The explicit submission: opening the video call shows a start screen, and nothing is
  // requested until the customer taps Start.
  const [started, setStarted] = useState(false);

  const requestCallQuery = useQuery({
    queryFn: () => {
      return AssetFrontendSupportVideoCallsApi.requestCall(
        uic.organisationContext.organisationId,
        assetView.urlParamsForRequests.viewId,
        assetView.urlParamsForRequests.viewValue,
        assetView.urlParamsForRequests.assetId,
      );
    },
    queryKey: [
      "AssetFrontendSupportVideoCallsApi",
      AssetFrontendSupportVideoCallsApi.requestCall.name,
      uic.organisationContext.organisationId,
      assetView.urlParamsForRequests.keyString,
    ],
    enabled: started,
    staleTime: Infinity,
    gcTime: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
  });

  // Set once the backend reports the session as Ended, either from the poll below or
  // because the customer left the room and the session had already ended on their way out.
  const [polledEnded, setPolledEnded] = useState(false);
  // The customer disconnected from the room on their own (e.g. pressed Leave). The session
  // itself may still be open, so this offers a way back in rather than a dead end.
  const [leftRoom, setLeftRoom] = useState(false);

  const data = requestCallQuery.data;
  const ended = polledEnded || data?.state === State.Ended;
  const joinable =
    !ended && !leftRoom && data != null && data.livekitToken != null &&
    (data.state === State.Waiting || data.state === State.Active);

  // The state the last successful poll reported. Kept in our own state rather than read from
  // pollState.data: every mutate() clears that while the next poll is in flight, which made
  // the Cancel button flash back every 1.5s once a worker had joined.
  const [polledState, setPolledState] =
    useState<AssetFrontendSupportVideoCallsApi$RequestCallResultState | null>(null);

  // Polling only detects Ended - the room itself shows the waiting state while Waiting.
  const pollState = useMutation({
    mutationFn: () =>
      AssetFrontendSupportVideoCallsApi.queryPending(
        uic.organisationContext.organisationId,
      ),
    onSuccess: (r) => {
      setPolledState(r.state);
      if (r.state === State.Ended) {
        setPolledEnded(true);
      }
    },
  });

  // The freshest state we know of, from whichever of the initial request or a later poll
  // last resolved - used to tell whether a not-yet-acted-on Cancel would be a no-op.
  const lastKnownState = polledState ?? data?.state ?? null;

  // Keep polling while the customer has stepped out of the room too, not just while it is
  // shown: otherwise a Rejoin button could outlive a session that ended while they were away.
  useEffect(() => {
    if (ended || (!joinable && !leftRoom)) return;
    const interval = setInterval(() => {
      pollState.mutate();
    }, 1500);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [joinable, leftRoom, ended]);

  // The backend only acts on a Waiting session - after a worker has joined this is a no-op,
  // which is correct, so it never force-closes an active call from under the worker.
  const cancelCall = useMutation({
    mutationFn: () =>
      AssetFrontendSupportVideoCallsApi.closeCallIfPending(
        uic.organisationContext.organisationId,
      ),
    onSuccess: () => pollState.mutate(),
    onError: () => pollState.mutate(),
  });

  const handleEnded = (info: VideoRoomEndedInfo) => {
    if (info.remoteClose) {
      setPolledEnded(true);
      return;
    }
    setLeftRoom(true);
  };

  const rejoin = () => setLeftRoom(false);

  return {
    started,
    start: () => setStarted(true),
    loading: started && requestCallQuery.isPending,
    loadError: requestCallQuery.isError,
    ended,
    leftRoom,
    joinable,
    // Cancel is only meaningful while Waiting - once a worker has joined it is a no-op.
    cancelIsNoOp: lastKnownState === State.Active,
    token: data?.livekitToken ?? null,
    serverUrl: data?.livekitServerUrl ?? null,
    recordingEnabled: data?.recordingEnabled === true,
    roomId: data?.roomId ?? null,
    cancelCall,
    handleEnded,
    rejoin,
  };
};

export default function VideoCallAppPageComponent(p: {
  component: PublicAssetFrontendApi$AssetComponentDto;
  assetView: AssetViewDetails;
  onUserRequestsCloseCall: () => void;
}) {
  const { t } = useTranslation();
  const vph = videoPageHook(p.assetView);
  // This is the customer's own asset page, so the embedded room follows the
  // asset-frontend switch, not the admin switch - see rule in the newdesign HANDOFF.
  const newDesign = useAssetFrontendDesign();

  // Nothing is requested until the customer actively starts the call.
  if (!vph.started) {
    return (
      <div className="mx-auto max-w-md py-12 text-center">
        <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[var(--rda-accent)] text-[var(--rda-on-accent)]">
          <PiVideoCamera aria-hidden className="text-3xl" />
        </span>
        <p className="mt-5 text-base text-[var(--rda-muted)]">
          {t("component.videocall.subtitlePrompt")}
        </p>
        <div className="mt-7 space-y-3">
          <PrimaryButton type="button" onClick={vph.start}>
            {p.component.name}
          </PrimaryButton>
          <SecondaryButton type="button" onClick={p.onUserRequestsCloseCall}>
            {t("component.videocall.cancel")}
          </SecondaryButton>
        </div>
      </div>
    );
  }

  if (vph.loading) {
    return (
      <div className="mx-auto max-w-md space-y-4 py-12 text-center">
        <h3 className="text-xl font-semibold tracking-[-0.02em]">
          {t("component.videocall.calling")}
        </h3>
        <p className="text-base text-[var(--rda-muted)]">
          {t("component.videocall.calling.description")}
        </p>
        {newDesign ? <RdaLoading compact /> : <Loading loading={true} />}
      </div>
    );
  }

  if (vph.loadError) {
    return (
      <div className="mx-auto max-w-md space-y-4 py-12 text-center">
        <h3 className="text-xl font-semibold tracking-[-0.02em]">
          {t("component.videocall.status.error")}
        </h3>
        <p className="text-base text-[var(--rda-muted)]">
          {t("component.videocall.status.error.description")}
        </p>
        <SecondaryButton type="button" onClick={p.onUserRequestsCloseCall}>
          {t("component.videocall.cancel")}
        </SecondaryButton>
      </div>
    );
  }

  if (vph.ended) {
    return (
      <div className="mx-auto max-w-md space-y-4 py-12 text-center">
        <h3 className="text-xl font-semibold tracking-[-0.02em]">
          {t("component.videocall.status.closed")}
        </h3>
        <p className="text-base text-[var(--rda-muted)]">
          {t("component.videocall.status.closed.description")}
        </p>
      </div>
    );
  }

  if (vph.leftRoom) {
    return (
      <div className="mx-auto max-w-md space-y-4 py-12 text-center">
        <h3 className="text-xl font-semibold tracking-[-0.02em]">
          {t("component.videocall.status.left")}
        </h3>
        <p className="text-base text-[var(--rda-muted)]">
          {t("component.videocall.status.left.description")}
        </p>
        <div className="space-y-3">
          <PrimaryButton type="button" onClick={vph.rejoin}>
            {t("component.videocall.rejoin")}
          </PrimaryButton>
          {!vph.cancelIsNoOp && (
            <SecondaryButton type="button" onClick={() => vph.cancelCall.mutate()}>
              {t("component.videocall.cancel")}
            </SecondaryButton>
          )}
        </div>
      </div>
    );
  }

  if (vph.joinable && vph.token != null && vph.serverUrl != null) {
    if (newDesign) {
      // The mobile technician room has no room for an absolutely positioned Cancel button
      // over the bottom call bar, so it moves into the room header instead. Same no-op rule
      // as the old design: once a worker has joined, cancelling does nothing on the backend.
      return (
        <div className="video-room-embedded relative h-full min-h-0">
          <MobileTechnicianRoom
            embedded
            token={vph.token}
            serverUrl={vph.serverUrl}
            recording={vph.recordingEnabled}
            title="Video support"
            roomId={vph.roomId ?? undefined}
            secondaryAction={
              vph.cancelIsNoOp
                ? undefined
                : { label: t("component.videocall.cancel"), onClick: () => vph.cancelCall.mutate() }
            }
            onEnded={vph.handleEnded}
          />
        </div>
      );
    }
    return (
      // Fills the drawer body rather than the viewport: the drawer header sits above it, so
      // a 100dvh box would run past the bottom of the drawer and hide the Cancel button.
      <div className="video-room-embedded relative h-full min-h-0">
        <VideoRoom
          token={vph.token}
          serverUrl={vph.serverUrl}
          role="customer"
          roomId={vph.roomId ?? undefined}
          recording={vph.recordingEnabled}
          onEnded={vph.handleEnded}
        />
        {/* Same rule as the left-room view: once a worker has joined, cancelling is a
            no-op on the backend, so do not offer a button that does nothing. */}
        {!vph.cancelIsNoOp && (
          <button
            type="button"
            className="rda-tile absolute bottom-4 left-1/2 z-10 flex -translate-x-1/2 items-center justify-center bg-[var(--rda-accent)] px-4 py-2.5 text-sm font-semibold text-[var(--rda-on-accent)]"
            onClick={() => vph.cancelCall.mutate()}
          >
            {t("component.videocall.cancel")}
          </button>
        )}
      </div>
    );
  }

  return null;
}
